import { useMemo, useState } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import { works } from "@/data/catalog";
import type { WorkType } from "@/types/work";
import {
  ALL_GENRES,
  collectYears,
  filterWorks,
  paginate,
  sortWorks,
  type SortKey,
} from "@/utils/workUtils";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

type EpisodeBand = "short" | "mid" | "long";

const RATING_OPTIONS: { label: string; value: number | null }[] = [
  { label: "不限评分", value: null },
  { label: "8.0 分以上", value: 8 },
  { label: "7.5 分以上", value: 7.5 },
  { label: "7.0 分以上", value: 7 },
];

const EPISODE_OPTIONS: { label: string; value: EpisodeBand | null }[] = [
  { label: "不限集数", value: null },
  { label: "10 集以内", value: "short" },
  { label: "11 - 16 集", value: "mid" },
  { label: "17 集以上", value: "long" },
];

const SORT_OPTIONS: { label: string; value: SortKey }[] = [
  { label: "最新", value: "latest" },
  { label: "热门", value: "popular" },
  { label: "评分", value: "rating" },
];

export interface CategoryConfig {
  type: WorkType;
  crumb: string;
  title: string;
  description: string;
  meta: { title: string; description: string; keywords: string };
}

interface Props {
  config: CategoryConfig;
}

export default function CategoryView({ config }: Props) {
  useDocumentMeta(config.meta);

  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [ratingMin, setRatingMin] = useState<number | null>(null);
  const [episodeBand, setEpisodeBand] = useState<EpisodeBand | null>(null);
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);

  const pool = useMemo(() => works.filter((w) => w.type === config.type), [config.type]);
  const yearOptions = useMemo(() => collectYears(pool), [pool]);
  const genresAvailable = useMemo(
    () => ALL_GENRES.filter((g) => pool.some((w) => w.genres.includes(g))),
    [pool],
  );

  const filtered = useMemo(() => {
    let list = filterWorks(pool, { genres: selectedGenres, years: selectedYears, ratingMin });
    if (config.type === "drama" && episodeBand) {
      list = list.filter((w) => {
        const eps = w.episodes ?? 0;
        if (episodeBand === "short") return eps <= 10;
        if (episodeBand === "mid") return eps >= 11 && eps <= 16;
        return eps >= 17;
      });
    }
    return sortWorks(list, sort);
  }, [pool, selectedGenres, selectedYears, ratingMin, episodeBand, sort, config.type]);

  const { pageItems, totalPages, currentPage } = paginate(filtered, page, 8);

  const activeCount =
    selectedGenres.length +
    selectedYears.length +
    (ratingMin !== null ? 1 : 0) +
    (episodeBand !== null ? 1 : 0);

  const toggleGenre = (genre: string) => {
    setSelectedGenres((prev) =>
      prev.includes(genre) ? prev.filter((g) => g !== genre) : [...prev, genre],
    );
    setPage(1);
  };

  const toggleYear = (year: number) => {
    setSelectedYears((prev) =>
      prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year],
    );
    setPage(1);
  };

  const resetFilters = () => {
    setSelectedGenres([]);
    setSelectedYears([]);
    setRatingMin(null);
    setEpisodeBand(null);
    setPage(1);
  };

  const chipClass = (active: boolean) =>
    `px-3 py-1.5 rounded-full text-sm border transition-colors cursor-pointer whitespace-nowrap ${
      active
        ? "bg-primary-500 text-background-50 border-primary-500"
        : "bg-background-50 text-foreground-700 border-background-300 hover:border-primary-300"
    }`;

  const filterBody = (
    <div className="flex flex-col gap-5">
      <div>
        <p className="text-xs font-medium text-foreground-500 mb-2">类型标签</p>
        <div className="flex flex-wrap gap-2">
          {genresAvailable.map((genre) => (
            <button
              key={genre}
              type="button"
              onClick={() => toggleGenre(genre)}
              className={chipClass(selectedGenres.includes(genre))}
            >
              {genre}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-medium text-foreground-500 mb-2">上映年份</p>
        <div className="flex flex-wrap gap-2">
          {yearOptions.map((year) => (
            <button
              key={year}
              type="button"
              onClick={() => toggleYear(year)}
              className={chipClass(selectedYears.includes(year))}
            >
              {year}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-medium text-foreground-500 mb-2">评分区间</p>
        <div className="flex flex-wrap gap-2">
          {RATING_OPTIONS.map((option) => (
            <button
              key={option.label}
              type="button"
              onClick={() => {
                setRatingMin(option.value);
                setPage(1);
              }}
              className={chipClass(ratingMin === option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {config.type === "drama" && (
        <div>
          <p className="text-xs font-medium text-foreground-500 mb-2">集数区间</p>
          <div className="flex flex-wrap gap-2">
            {EPISODE_OPTIONS.map((option) => (
              <button
                key={option.label}
                type="button"
                onClick={() => {
                  setEpisodeBand(option.value);
                  setPage(1);
                }}
                className={chipClass(episodeBand === option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: config.crumb }]} />

      <header className="mt-5 mb-6">
        <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">
          {config.title}
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
          {config.description}
        </p>
      </header>

      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="rounded-lg border border-background-200 bg-background-100/50 p-5 sticky top-20">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-foreground-950">筛选条件</h2>
              {activeCount > 0 && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-xs text-accent-600 hover:text-accent-700 cursor-pointer whitespace-nowrap"
                >
                  重置
                </button>
              )}
            </div>
            {filterBody}
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <p className="text-sm text-foreground-600">
              共 <span className="text-foreground-950 font-semibold">{filtered.length}</span> 部作品
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPanelOpen(true)}
                className="lg:hidden h-9 px-4 rounded-md border border-background-300 text-sm text-foreground-700 flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-filter-3-line" /> 筛选
                {activeCount > 0 && (
                  <span className="min-w-4 h-4 px-1 rounded-full bg-accent-500 text-background-50 text-[10px] flex items-center justify-center">
                    {activeCount}
                  </span>
                )}
              </button>
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
                {SORT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setSort(option.value);
                      setPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      sort === option.value
                        ? "bg-primary-500 text-background-50"
                        : "text-foreground-600 hover:text-primary-700"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100/50 py-20 flex flex-col items-center gap-3 text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-search-eye-line text-3xl" />
              </span>
              <p className="font-medium text-foreground-800">
                未找到符合条件的{config.type === "movie" ? "伦理电影" : "伦理电视剧"}作品
              </p>
              <p className="text-sm text-foreground-500">试着减少筛选条件，或重置后重新浏览。</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-1 px-5 h-10 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
              >
                重置筛选
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                {pageItems.map((work) => (
                  <WorkCard key={work.id} work={work} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="h-9 px-3 rounded-md border border-background-300 text-sm text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-s-line" /> 上一页
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPage(i + 1)}
                      className={`w-9 h-9 rounded-md text-sm transition-colors cursor-pointer ${
                        currentPage === i + 1
                          ? "bg-primary-500 text-background-50"
                          : "border border-background-300 text-foreground-700 hover:border-primary-300"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="h-9 px-3 rounded-md border border-background-300 text-sm text-foreground-700 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-300 cursor-pointer whitespace-nowrap"
                  >
                    下一页 <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {panelOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setPanelOpen(false)} />
          <div className="absolute bottom-0 inset-x-0 bg-background-50 rounded-t-2xl max-h-[85vh] flex flex-col">
            <div className="h-14 px-5 flex items-center justify-between border-b border-background-200 shrink-0">
              <span className="font-heading font-semibold text-foreground-950">筛选条件</span>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-800 cursor-pointer"
                aria-label="关闭筛选"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto">{filterBody}</div>
            <div className="p-4 border-t border-background-200 flex items-center gap-3 shrink-0">
              <button
                type="button"
                onClick={resetFilters}
                className="flex-1 h-11 rounded-md border border-background-300 text-foreground-700 text-sm font-medium cursor-pointer"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                className="flex-1 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer"
              >
                查看 {filtered.length} 部作品
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}